import React from 'react';
import Icon from '../../../components/AppIcon';

const ValuesSection = () => {
  const values = [
    {
      icon: "Target",
      title: "Client-Centric Excellence",
      description: "Every decision we make starts with one question: How does this serve our clients' success? We measure our achievements not in code deployed, but in business outcomes delivered and partnerships strengthened.",
      color: "primary"
    },
    {
      icon: "Lightbulb",
      title: "Innovation with Purpose",
      description: "We embrace cutting-edge technologies not for novelty, but for the tangible value they bring. Our innovation is always grounded in solving real business challenges and creating measurable competitive advantages.",
      color: "accent"
    },
    {
      icon: "Shield",
      title: "Transparent Partnership",
      description: "Trust is built through transparency. We share our processes, communicate openly about challenges, and maintain honest dialogue throughout every project. No surprises, no hidden agendas—just authentic collaboration.",
      color: "success"
    },
    {
      icon: "Zap",
      title: "Technical Mastery",
      description: "Excellence in execution is non-negotiable. Our team continuously sharpens their craft, staying ahead of industry trends while maintaining deep expertise in proven technologies that deliver reliable, scalable solutions.",
      color: "warning"
    },
    {
      icon: "Users",
      title: "Collaborative Growth",
      description: "We believe in growing together—with our clients, our team, and our industry. Every project is an opportunity for mutual learning, knowledge sharing, and building capabilities that extend beyond the engagement.",
      color: "primary"
    },
    {
      icon: "Heart",
      title: "Long-term Commitment",
      description: "We're not interested in transactional relationships. Our goal is to become your trusted technology advisor for years to come, supporting your evolution from startup to scale-up and beyond.",
      color: "destructive"
    }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4 md:mb-6">
            <Icon name="Compass" size={18} color="var(--color-primary)" />
            <span className="text-sm font-medium font-body text-primary">Our Values</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-4 md:mb-6">
            Principles That Guide Everything We Do
          </h2>
          
          <p className="text-base md:text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Our values aren't just words on a wall—they're the foundation of every client interaction, every line of code, and every strategic decision we make.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {values?.map((value, index) => (
            <div
              key={index}
              className="bg-card p-6 md:p-8 rounded-xl shadow-md border border-border hover:shadow-xl transition-all duration-300 group"
            >
              <div className="w-14 h-14 md:w-16 md:h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Icon name={value?.icon} size={28} color="var(--color-primary)" />
              </div>
              
              <h3 className="text-lg md:text-xl font-semibold font-headline text-foreground mb-3 md:mb-4">
                {value?.title}
              </h3>
              
              <p className="text-sm md:text-base text-muted-foreground font-body leading-relaxed">
                {value?.description}
              </p>
            </div>
          ))}
        </div>
        
        <div className="mt-12 md:mt-16 bg-gradient-to-br from-primary/5 to-accent/5 p-6 md:p-8 lg:p-10 rounded-2xl border border-border">
          <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
            <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon name="Quote" size={32} color="var(--color-primary)" />
            </div>
            
            <div className="flex-1">
              <p className="text-base md:text-lg lg:text-xl text-foreground font-body italic leading-relaxed mb-3">
              "Recognition validates our approach, but our true measure of success is the transformation we enable for our clients. Every award represents countless hours of dedication, collaboration, and commitment to excellence."
              </p>
              <p className="text-sm md:text-base text-muted-foreground font-body">
                — Leadership Team, Devontis Technologies
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValuesSection;